import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Mail, MessageCircle } from "lucide-react";
import Logo from "../Logo";
import { useI18n } from "../../i18n/LanguageProvider";
import { SITE, WHATSAPP_LINK } from "../../config/site";
import { TREATMENTS } from "../../data/treatments";
import { LOCATIONS } from "../../data/locations";

export default function Footer() {
  const { t, p, e, lang } = useI18n();
  const fl = t.footer.links;

  const col = (title, links) => (
    <div>
      <h3 className="text-sm font-semibold uppercase tracking-[0.15em] text-white/50 mb-4">
        {title}
      </h3>
      <ul className="space-y-2.5">
        {links.map((l) => (
          <li key={l.to + l.label}>
            <Link
              to={l.to}
              data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="text-[15px] text-white/70 hover:text-turquoise transition-colors"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <footer className="bg-navy text-white relative overflow-hidden" data-testid="site-footer">
      <div className="container-dv py-16 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Logo variant="light" />
            <p className="mt-6 text-[15px] leading-relaxed text-white/65 max-w-sm">
              {t.footer.about}
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a href={SITE.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-turquoise transition-colors">
                <Facebook className="h-4.5 w-4.5" />
              </a>
              <a href={SITE.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-turquoise transition-colors">
                <Instagram className="h-4.5 w-4.5" />
              </a>
              <a href={SITE.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-turquoise transition-colors">
                <Linkedin className="h-4.5 w-4.5" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-8 grid grid-cols-2 gap-8 sm:grid-cols-4">
            {col(t.footer.treatments, [
              ...TREATMENTS.slice(0, 5).map((tr) => ({ to: e(tr.slug), label: tr.name[lang] || tr.name.en })),
            ])}
            {col(t.footer.locations, [
              ...LOCATIONS.map((lo) => ({ to: e(lo.slug), label: lo.name[lang] || lo.name.en })),
              { to: p("dentalTourism"), label: t.nav.locations },
            ])}
            {col(t.footer.company, [
              { to: p("about"), label: fl.about },
              { to: p("howWeSelect"), label: fl.howWeSelect },
              { to: p("blog"), label: fl.blog },
              { to: p("patientGuide"), label: fl.patientGuide },
              { to: p("contact"), label: fl.contact },
            ])}
            {col(t.footer.legal, [
              { to: p("editorial"), label: fl.editorial },
              { to: p("medicalReview"), label: fl.medicalReview },
              { to: p("disclaimer"), label: fl.disclaimer },
              { to: p("privacy"), label: fl.privacy },
              { to: p("terms"), label: fl.terms },
            ])}
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-4 sm:flex-row sm:items-center">
          <a href={`mailto:${SITE.email}`} className="inline-flex items-center gap-2 text-[15px] text-white/70 hover:text-turquoise transition-colors" data-testid="footer-email">
            <Mail className="h-4 w-4" /> {SITE.email}
          </a>
          <a href={WHATSAPP_LINK()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[15px] text-white/70 hover:text-turquoise transition-colors" data-testid="footer-whatsapp">
            <MessageCircle className="h-4 w-4" /> {t.cta.whatsapp}
          </a>
        </div>

        <div className="mt-10 rounded-2xl bg-white/5 p-5 text-[13px] leading-relaxed text-white/55">
          {t.footer.disclaimerShort}
        </div>

        <div className="mt-8 border-t border-white/10 pt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-[13px] text-white/50">
          <p>© {new Date().getFullYear()} {SITE.brand}. {t.footer.rights}</p>
          <p>dentel-vietnam.com</p>
        </div>
      </div>
    </footer>
  );
}
